import { Injectable } from '@angular/core';

/**
 * Tracking data captured from URL query params and Facebook cookies.
 * Stored in a first-party cookie for use with CAPI on signup.
 */
export interface TrackingData {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;
  /** The full original URL the user landed on (captured when UTM params are present) */
  originalUrl?: string;
}

/**
 * Captures UTM params, fbclid, and Facebook browser cookies (_fbp, _fbc)
 * on landing and persists them in a first-party cookie.
 *
 * Facebook Pixel sets _fbp asynchronously after the script loads, so this
 * service polls for it in the background and updates the stored data once
 * the real values become available.
 */
@Injectable({ providedIn: 'root' })
export class TrackingCookieService {
  private readonly COOKIE_NAME = '_jt_tracking';
  private readonly EXPIRY_DAYS = 30;

  /**
   * Call once on app bootstrap (e.g. APP_INITIALIZER or root component).
   *
   * 1. Reads URL query params and any already-available FB cookies.
   * 2. Builds fallback _fbc from fbclid if FB hasn't set one yet.
   * 3. Persists immediately so the data is available even if the user
   *    signs up before the pixel script finishes loading.
   * 4. Starts async polling for the real _fbp / _fbc values and updates
   *    the cookie when they appear.
   */
  captureFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const data: TrackingData = this.getTrackingData();

    // Capture UTM params (last-touch: overwrite if present in URL)
    const utmSource = params.get('utm_source');
    if (utmSource) data.utmSource = utmSource;

    const utmMedium = params.get('utm_medium');
    if (utmMedium) data.utmMedium = utmMedium;

    const utmCampaign = params.get('utm_campaign');
    if (utmCampaign) data.utmCampaign = utmCampaign;

    const utmContent = params.get('utm_content');
    if (utmContent) data.utmContent = utmContent;

    const utmTerm = params.get('utm_term');
    if (utmTerm) data.utmTerm = utmTerm;

    // Store the full original URL when any UTM param is present
    if (utmSource || utmMedium || utmCampaign || utmContent || utmTerm) {
      data.originalUrl = window.location.href;
    }

    // Capture fbclid and build fallback _fbc
    const fbclid = params.get('fbclid');
    if (fbclid) {
      data.fbclid = fbclid;
      // Build _fbc format: fb.1.{timestamp}.{fbclid}
      data.fbc = `fb.1.${Date.now()}.${fbclid}`;
    }

    // Try to read FB cookies right away (may not exist yet)
    const fbp = this.getCookie('_fbp');
    if (fbp) data.fbp = fbp;

    const existingFbc = this.getCookie('_fbc');
    if (existingFbc) data.fbc = existingFbc;

    // Persist immediately with whatever we have
    this.saveToCookie(data);

    // Start async polling for the real FB cookies (pixel loads asynchronously)
    this.pollForFbCookies(data);
  }

  /**
   * Returns the current tracking data from the cookie.
   */
  getTrackingData(): TrackingData {
    const raw = this.getCookie(this.COOKIE_NAME);
    if (!raw) return {};
    try {
      return JSON.parse(decodeURIComponent(raw));
    } catch {
      return {};
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Polls for _fbp (and _fbc) cookies that Facebook Pixel sets asynchronously.
   * If found within the retry window, updates the stored tracking cookie.
   */
  private async pollForFbCookies(
    data: TrackingData,
    maxRetries = 10,
    delayMs = 500,
  ): Promise<void> {
    for (let i = 0; i < maxRetries; i++) {
      const fbp = this.getCookie('_fbp');
      const fbc = this.getCookie('_fbc');

      if (fbp) {
        data.fbp = fbp;
        if (fbc) data.fbc = fbc;
        this.saveToCookie(data);
        return;
      }

      await this.delay(delayMs);
    }

    // Final attempt after all retries
    const fbp = this.getCookie('_fbp');
    const fbc = this.getCookie('_fbc');
    if (fbp) data.fbp = fbp;
    if (fbc) data.fbc = fbc;
    this.saveToCookie(data);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private saveToCookie(data: TrackingData): void {
    const expires = new Date();
    expires.setDate(expires.getDate() + this.EXPIRY_DAYS);
    const value = encodeURIComponent(JSON.stringify(data));
    document.cookie = `${this.COOKIE_NAME}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  }

  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${name}=([^;]*)`),
    );
    return match ? decodeURIComponent(match[1]) : null;
  }
}
